import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { communityHostKeys, type CoHostCommunity } from '@/types/communityHost';
import { clientPaths } from '@/utils/clientPaths';
import { hostPaths } from '@/utils/hostPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import showToast from '@/components/ui/Toast';
import { HostConfirmDialog } from '@/components/community/host-management/HostDialogs';

export function ClientCoHostCommunitiesPage() {
  const navigate = useNavigate();
  const { enterCommunity } = useAuth();
  const [selected, setSelected] = useState<CoHostCommunity | null>(null);
  const [entering, setEntering] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: communityHostKeys.communities,
    queryFn: () => authService.listCoHostCommunities(),
  });

  const items = data ?? [];

  const handleEnter = async () => {
    if (!selected) return;
    setEntering(true);
    try {
      await enterCommunity(selected.community_id);
      navigate(hostPaths.home, { replace: true });
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Gagal masuk sebagai co-host'));
    } finally {
      setEntering(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-full text-[#0E1726]">
      <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4 pb-8">
        <div className="hidden md:flex items-center gap-3 py-2">
          <button
            type="button"
            onClick={() => navigate(clientPaths.profile.account)}
            className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center hover:bg-gray-50 cursor-pointer"
            aria-label="Kembali"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <h1 className="text-lg md:text-xl font-bold">Kelola Komunitas</h1>
        </div>

        {isLoading && (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center text-xs text-gray-400">
            Memuat komunitas...
          </div>
        )}

        {(isError || (!isLoading && items.length === 0)) && (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center space-y-2">
            <p className="text-sm font-bold text-[#0E1726]">Belum ada komunitas</p>
            <p className="text-xs text-gray-400 font-medium">
              Kamu belum diangkat sebagai co-host di komunitas mana pun.
            </p>
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden divide-y divide-gray-100">
            {items.map((item) => (
              <button
                key={item.community_id}
                type="button"
                onClick={() => setSelected(item)}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-left hover:bg-gray-50/80 cursor-pointer"
              >
                {item.logo ? (
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="w-11 h-11 rounded-full object-cover border border-gray-100 shrink-0"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                    <Icon icon="ph:users-three-bold" className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold truncate">{item.name}</p>
                  <p className="text-xs text-gray-400 font-medium mt-0.5 truncate">
                    {item.sport_category_name || 'Komunitas'}
                  </p>
                </div>
                <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      <HostConfirmDialog
        open={!!selected}
        title="Masuk sebagai Co-Host"
        message={`Masuk sebagai co-host ${selected?.name ?? ''}? Kamu akan mengelola komunitas ini.`}
        confirmLabel="Masuk"
        loading={entering}
        onCancel={() => setSelected(null)}
        onConfirm={() => { void handleEnter(); }}
      />
    </div>
  );
};

export default ClientCoHostCommunitiesPage;
